/**
 * Phase 13.4a (migration 0236) — a tank's haul threshold on the Sites & Operators tank forms
 * (AddTankModal's edit mode and WellDetail's standalone-tank form).
 *
 *   Haul at (% full)   empty = no threshold
 *   Lead time (hours)  act this many hours before the tank is predicted to reach it (24 default)
 *   Auto-haul job type the job type an auto job gets (none = no auto job for this tank), kept as
 *                      its family: editing the job type makes a new version, and the tank follows
 *
 * Auto-haul itself is also switched on per site and is a plan feature (AUTO_HAUL), so the form only
 * says what the threshold is; the Tanks tab shows when the tank is predicted to reach it.
 */

import * as React from 'react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { FormRow, fieldStyle } from './form-bits';
import { useJobTypes } from '../jobs/use-job-types';

export interface TankThresholdValues {
  readonly percent: string;
  readonly leadHours: string;
  /** A job type family id ('' = none). */
  readonly jobTypeFamilyId: string;
}

export function thresholdValuesFrom(
  t: {
    thresholdPercent?: string | null;
    leadTimeHours?: number;
    autoHaulJobTypeFamilyId?: string | null;
  } | null,
): TankThresholdValues {
  return {
    percent: t?.thresholdPercent == null ? '' : String(Number(t.thresholdPercent)),
    leadHours: String(t?.leadTimeHours ?? 24),
    jobTypeFamilyId: t?.autoHaulJobTypeFamilyId ?? '',
  };
}

/** The PATCH fields, or an error message to show instead. */
export function thresholdPatch(v: TankThresholdValues):
  | {
      ok: true;
      body: {
        thresholdPercent: number | null;
        leadTimeHours: number;
        autoHaulJobTypeFamilyId: string | null;
      };
    }
  | { ok: false; error: string } {
  const pct = v.percent.trim() === '' ? null : Number(v.percent);
  if (pct !== null && (!Number.isFinite(pct) || pct < 1 || pct > 100)) {
    return { ok: false, error: 'Haul at must be between 1 and 100 percent, or empty for none.' };
  }
  const lead = Number(v.leadHours);
  if (!Number.isInteger(lead) || lead < 1 || lead > 720) {
    return { ok: false, error: 'Lead time must be a whole number of hours from 1 to 720.' };
  }
  return {
    ok: true,
    body: {
      thresholdPercent: pct,
      leadTimeHours: lead,
      autoHaulJobTypeFamilyId: v.jobTypeFamilyId === '' ? null : v.jobTypeFamilyId,
    },
  };
}

export function TankThresholdFields({
  value,
  onChange,
  idPrefix,
}: {
  value: TankThresholdValues;
  onChange: (next: TankThresholdValues) => void;
  idPrefix: string;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data } = useJobTypes(apiOpts);
  const jobTypes = data?.jobTypes ?? [];
  // A stored job type that is no longer offered (switched off) still shows, so a save keeps it.
  const known =
    value.jobTypeFamilyId === '' || jobTypes.some((jt) => jt.familyId === value.jobTypeFamilyId);
  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: '12px' }}>
      <legend style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
        Haul threshold
      </legend>
      <div style={{ display: 'flex', gap: '12px' }}>
        <FormRow label="Haul at (% full)" htmlFor={`${idPrefix}-pct`}>
          <input
            id={`${idPrefix}-pct`}
            inputMode="decimal"
            value={value.percent}
            placeholder="None"
            onChange={(e): void => onChange({ ...value, percent: e.target.value })}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Lead time (hours)" htmlFor={`${idPrefix}-lead`}>
          <input
            id={`${idPrefix}-lead`}
            inputMode="numeric"
            value={value.leadHours}
            onChange={(e): void => onChange({ ...value, leadHours: e.target.value })}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      <FormRow label="Auto-haul job type" htmlFor={`${idPrefix}-jt`}>
        <select
          id={`${idPrefix}-jt`}
          value={value.jobTypeFamilyId}
          onChange={(e): void => onChange({ ...value, jobTypeFamilyId: e.target.value })}
          style={fieldStyle}
        >
          <option value="">None (no auto job)</option>
          {!known && <option value={value.jobTypeFamilyId}>A job type no longer offered</option>}
          {jobTypes.map((jt) => (
            <option key={jt.familyId} value={jt.familyId}>
              {jt.label}
            </option>
          ))}
        </select>
      </FormRow>
    </fieldset>
  );
}
